import { colors } from '@packages/core/styles';
import { WorksheetAnswerStatus } from '@packages/models/api';
import { Typography } from '@packages/ui/shared';
import React, { useCallback, useMemo, useState } from 'react';
import { Accordion, Card, Form } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';

import type { WorksheetGroup } from './worksheet-group-accordion.types';

import { WorksheetItemCard } from './worksheet-item-card';

const useStyles = createUseStyles({
    accordionCard: {
        marginBottom: 16,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: colors.white,
    },
    accordionHeader: {
        padding: '20px 24px',
        cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns: '24px 1fr',
        gap: 16,
        alignItems: 'start',
        backgroundColor: colors.white,
        border: 'none',
        outline: 'none',
        textAlign: 'left',
        width: '100%',
        minHeight: 90,
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: colors.grayOne,
        },
        '&:focus:not(:focus-visible)': {
            outline: 'none',
        },
    },
    accordionHeaderDisabled: {
        '&&': {
            cursor: 'default',
        },
        '&:hover': {
            backgroundColor: colors.white,
        },
    },
    checkboxContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        height: 26,
        '& .form-check': {
            marginTop: 0,
            paddingTop: 0,
            height: 26,
            display: 'flex',
            alignItems: 'center',
        },
        '& .form-check-label': {
            display: 'inline',
            padding: '3px 0 !important',
        },
        '& .form-check-input': {
            marginTop: 0,
        },
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    accordionBody: {
        padding: '0 24px 24px 24px',
        backgroundColor: colors.white,
    },
    accordionBodyContent: {
        marginLeft: 40, // 24px checkbox + 16px gap
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '20px 0',
        color: colors.grayFive,
    },
    spinner: {
        width: 20,
        height: 20,
        border: `3px solid ${colors.grayThree}`,
        borderTop: `3px solid ${colors.blueOne}`,
        borderRadius: '50%',
        animation: '$spin 1s linear infinite',
    },
    '@keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' },
    },
    errorContainer: {
        padding: '20px 0',
        color: colors.redOne,
    },
    skeletonCard: {
        backgroundColor: colors.grayOne,
        borderRadius: 8,
        padding: '20px 24px',
        marginBottom: 12,
        animation: '$pulse 1.5s ease-in-out infinite',
        '&:last-child': {
            marginBottom: 0,
        },
    },
    skeletonTitle: {
        width: '60%',
        height: 18,
        backgroundColor: colors.grayThree,
        borderRadius: 4,
        marginBottom: 8,
    },
    skeletonDescription: {
        width: '80%',
        height: 14,
        backgroundColor: colors.grayThree,
        borderRadius: 4,
    },
    '@keyframes pulse': {
        '0%': { opacity: 1 },
        '50%': { opacity: 0.5 },
        '100%': { opacity: 1 },
    },
});

/** Props for the WorksheetGroupAccordion component. */
export type WorksheetGroupAccordionProps = {
    /** The worksheet group data to display. */
    group: WorksheetGroup;

    /** Whether any worksheet in the case has been submitted (across all groups). */
    hasAnyWorksheetSubmitted?: boolean;

    /** Instruction text displayed above the worksheet list inside the accordion body. */
    instructionText?: string | null;

    /** Whether the parent case is in a closed status (CLOSED, CLOSED-RESOLVED, etc.). */
    isCaseClosed?: boolean;

    /** Whether the parent case is in DRAFT status. */
    isDraftCase?: boolean;

    /** Whether this group's checkbox is currently selected. */
    isSelected: boolean;

    /** Callback to navigate to and complete a worksheet by its ID. */
    onCompleteWorksheet: (worksheetId: string) => Promise<void>;

    /** Callback to delete a worksheet submission. */
    onDeleteWorksheet?: (worksheetId: string, submissionId: string) => void;

    /** Callback to download a worksheet submission as a file. */
    onDownloadWorksheet?: (worksheetId: string, submissionId: string) => Promise<void>;

    /** Callback to open a worksheet submission for editing. */
    onEditWorksheet?: (worksheetId: string, submissionId: string) => Promise<void>;

    /** Callback invoked when the accordion is expanded to trigger lazy-loading of worksheet titles. */
    onExpand?: (groupId: string) => void;

    /** Callback to toggle the group's selected state. */
    onGroupSelect: (groupId: string, isSelected: boolean) => void;

    /** Callback to view a submitted worksheet in read-only mode. */
    onViewWorksheet?: (worksheetId: string, submissionId: string) => Promise<void>;

    /** Callback to toggle an individual worksheet's selected state. */
    onWorksheetSelect: (worksheetId: string, isSelected: boolean) => void;

    /** The list of currently selected worksheet IDs within this group. */
    selectedWorksheets: string[];

    /** Whether to show the instruction text above the worksheet list. */
    showInstructionText?: boolean;

    /** Map of worksheet ID to validation result (true = valid, false = missing required info). */
    worksheetValidationResults?: Record<string, boolean>;
};

/** Style for the instruction text above worksheet cards. */
const worksheetsInstructionStyle = { marginBottom: 20 };

/**
 * Expandable accordion card for a worksheet group. Renders a checkbox header with the group name
 * and description, and an expandable body listing the individual worksheet item cards. Handles
 * auto-expansion when worksheets are saved or the group is selected.
 *
 * @param props - component props
 * @returns the worksheet group accordion
 */
export function WorksheetGroupAccordion({
    group,
    hasAnyWorksheetSubmitted = false,
    instructionText = 'Complete at least one Worksheet with details and diagnostics about the concern.',
    isCaseClosed = false,
    isDraftCase = false,
    isSelected,
    onCompleteWorksheet,
    onDeleteWorksheet,
    onDownloadWorksheet,
    onEditWorksheet,
    onExpand,
    onGroupSelect,
    onViewWorksheet,
    onWorksheetSelect,
    selectedWorksheets,
    showInstructionText = true,
    worksheetValidationResults = {},
}: WorksheetGroupAccordionProps) {
    const classes = useStyles();

    // Check if any worksheet in this group has been saved (has a submissionId)
    const hasAnyWorksheetSaved = group.worksheetTitles.some((worksheet) => Boolean(worksheet.submissionId));

    // Expand by default if the group has any submitted worksheets
    const [isExpanded, setIsExpanded] = useState(hasAnyWorksheetSaved);
    const isInitialMountRef = React.useRef(true);
    const prevHasAnyWorksheetSavedRef = React.useRef(hasAnyWorksheetSaved);

    // Mark that we've completed initial mount
    React.useEffect(() => {
        isInitialMountRef.current = false;
    }, []);

    // Auto-expand when a new worksheet is submitted (hasAnyWorksheetSaved changes from false to true)
    React.useEffect(() => {
        if (hasAnyWorksheetSaved && !prevHasAnyWorksheetSavedRef.current) {
            setIsExpanded(true);
        }
        prevHasAnyWorksheetSavedRef.current = hasAnyWorksheetSaved;
    }, [hasAnyWorksheetSaved]);

    // Sync expansion with selection: expand when checked, collapse when unchecked
    const prevIsSelectedRef = React.useRef(isSelected);
    React.useEffect(() => {
        if (isSelected !== prevIsSelectedRef.current) {
            setIsExpanded(isSelected);
        }
        prevIsSelectedRef.current = isSelected;
    }, [isSelected]);

    // Sorts worksheets by selection and then by name to bring current selections to top.
    const sortedWorksheets = useMemo(() => {
        return [...group.worksheetTitles].sort(
            (a, b) =>
                Number(selectedWorksheets.includes(b.worksheetId)) -
                    Number(selectedWorksheets.includes(a.worksheetId)) || a.displayName.localeCompare(b.displayName)
        );
    }, [group, selectedWorksheets]);

    /**
     * Handle the accordion header click. When the group is not disabled, toggles the checkbox
     * and sets expansion to match the new checkbox state. Disabled groups (with saved worksheets)
     * are non-interactive.
     *
     * @param e - the click event
     */
    const handleHeaderClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();

            // Disabled groups are non-interactive
            if (hasAnyWorksheetSaved) {
                return;
            }

            // Save current scroll position
            const scrollY = window.scrollY;
            const scrollX = window.scrollX;

            // Toggle checkbox; expansion follows the new checkbox state
            const willBeSelected = !isSelected;
            onGroupSelect(group.worksheetCaseGroupId, willBeSelected);
            setIsExpanded(willBeSelected);

            // Restore scroll position immediately
            requestAnimationFrame(() => {
                window.scrollTo(scrollX, scrollY);
            });

            // Call onExpand when expanding (not collapsing) and not during initial mount
            if (willBeSelected && onExpand && !isInitialMountRef.current) {
                onExpand(group.worksheetCaseGroupId);
            }
        },
        [group.worksheetCaseGroupId, hasAnyWorksheetSaved, isSelected, onExpand, onGroupSelect]
    );

    /**
     * Stop checkbox click events from propagating to the accordion header.
     *
     * @param e - the click event
     */
    const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    /**
     * Handle the group checkbox change. Prevents changes if any worksheet has been saved.
     *
     * @param e - the checkbox change event
     */
    const handleCheckboxChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            // Prevent changes if any worksheet has been saved
            if (hasAnyWorksheetSaved) {
                return;
            }
            onGroupSelect(group.worksheetCaseGroupId, e.target.checked);
        },
        [group.worksheetCaseGroupId, hasAnyWorksheetSaved, onGroupSelect]
    );

    /**
     * Handle keyboard navigation on the accordion header. Space or Enter behaves
     * identically to a click: toggles the checkbox and couples expansion to the
     * new checkbox state. Disabled groups are non-interactive.
     *
     * @param e - the keyboard event
     */
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key !== ' ' && e.key !== 'Enter') {
                return;
            }

            e.preventDefault();
            e.stopPropagation();

            // Disabled groups are non-interactive
            if (hasAnyWorksheetSaved) {
                return;
            }

            // Save current scroll position
            const scrollY = window.scrollY;
            const scrollX = window.scrollX;

            // Toggle checkbox; expansion follows the new checkbox state
            const willBeSelected = !isSelected;
            onGroupSelect(group.worksheetCaseGroupId, willBeSelected);
            setIsExpanded(willBeSelected);

            // Restore scroll position immediately
            requestAnimationFrame(() => {
                window.scrollTo(scrollX, scrollY);
            });

            // Call onExpand when expanding (not collapsing)
            if (willBeSelected && onExpand) {
                onExpand(group.worksheetCaseGroupId);
            }
        },
        [group.worksheetCaseGroupId, hasAnyWorksheetSaved, isSelected, onExpand, onGroupSelect]
    );

    /** Prevent mousedown default to avoid focus ring on click. */
    const handleHeaderMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
    }, []);

    return (
        <Accordion activeKey={isExpanded ? '0' : undefined}>
            <Card className={classes.accordionCard}>
                <button
                    aria-controls={`accordion-body-${group.worksheetCaseGroupId}`}
                    aria-expanded={isExpanded}
                    className={`${classes.accordionHeader} ${hasAnyWorksheetSaved ? classes.accordionHeaderDisabled : ''}`}
                    onClick={handleHeaderClick}
                    onKeyDown={handleKeyDown}
                    onMouseDown={handleHeaderMouseDown}
                    tabIndex={0}
                    type="button"
                >
                    <div className={classes.checkboxContainer} onClick={handleCheckboxClick}>
                        <Form.Check
                            aria-label={group.groupName}
                            checked={isSelected}
                            disabled={hasAnyWorksheetSaved}
                            id={`group-${group.worksheetCaseGroupId}`}
                            label=""
                            onChange={handleCheckboxChange}
                            tabIndex={-1}
                            type="checkbox"
                        />
                    </div>
                    <div className={classes.headerContent}>
                        <Typography color="textDarkPrimary" variant="caption3">
                            {group.groupName}
                        </Typography>
                        <Typography color="textDarkSecondary">{group.groupDescription}</Typography>
                    </div>
                </button>
                <Accordion.Collapse eventKey="0">
                    <Card.Body
                        aria-labelledby={`group-${group.worksheetCaseGroupId}`}
                        className={classes.accordionBody}
                        id={`accordion-body-${group.worksheetCaseGroupId}`}
                        role="region"
                    >
                        <div className={classes.accordionBodyContent}>
                            {group.isLoading ? (
                                <>
                                    {showInstructionText && instructionText && (
                                        <Typography style={worksheetsInstructionStyle} variant="caption3">
                                            {instructionText}
                                        </Typography>
                                    )}
                                    {/* Skeleton loader */}
                                    {[1, 2, 3].map((i) => (
                                        <div className={classes.skeletonCard} key={i}>
                                            <div className={classes.skeletonTitle} />
                                            <div className={classes.skeletonDescription} />
                                        </div>
                                    ))}
                                </>
                            ) : group.hasError ? (
                                <div className={classes.errorContainer}>
                                    Failed to load worksheets for this group. Please try again later.
                                </div>
                            ) : (
                                <>
                                    {showInstructionText && instructionText && (
                                        <Typography style={worksheetsInstructionStyle} variant="caption3">
                                            {instructionText}
                                        </Typography>
                                    )}
                                    {sortedWorksheets.map((worksheet) => {
                                        // Derive hasMissingInfo from API validation results
                                        let hasMissingInfo = false;

                                        // Worksheets are required to be submitted in cases that are not in a DRAFT status
                                        let doesNeedSubmission = false;

                                        if (
                                            worksheet.submissionId &&
                                            worksheet.status === WorksheetAnswerStatus.Draft &&
                                            worksheet.submissionId in worksheetValidationResults
                                        ) {
                                            hasMissingInfo = !worksheetValidationResults[worksheet.submissionId];
                                            if (!hasMissingInfo && !isDraftCase) {
                                                doesNeedSubmission = true;
                                            }
                                        }

                                        return (
                                            <WorksheetItemCard
                                                doesNeedSubmission={doesNeedSubmission}
                                                hasMissingInfo={hasMissingInfo}
                                                isCaseClosed={isCaseClosed}
                                                isDraftCase={isDraftCase}
                                                isRequired={!hasAnyWorksheetSubmitted && !hasAnyWorksheetSaved}
                                                isSelected={selectedWorksheets.includes(worksheet.worksheetId)}
                                                key={worksheet.submissionId ?? worksheet.worksheetId}
                                                onCompleteWorksheet={onCompleteWorksheet}
                                                onDeleteWorksheet={onDeleteWorksheet}
                                                onDownloadWorksheet={onDownloadWorksheet}
                                                onEditWorksheet={onEditWorksheet}
                                                onSelect={onWorksheetSelect}
                                                onViewWorksheet={onViewWorksheet}
                                                showSelectedStyling={showInstructionText}
                                                worksheet={worksheet}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    </Card.Body>
                </Accordion.Collapse>
            </Card>
        </Accordion>
    );
}
